<template>
    <!-- v-if="activeComponent" makes the entire container vanish when empty -->
    <div v-if="activeComponent" class="parent-cta-modal-container" ref="modalContainer">
        <div class="modal-content">
            <component
                :is="activeComponent"
                :dataDictionary="computedDataDictionary"
                @mouseenter-child="setActivePreview"
                @mouseleave-child="setActivePreviewDestroy"
            />
        </div>
    </div>

    <footer class="footer">
        <p>{{ profileAgeLabel }}</p>
    </footer>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import home from '@/data/home.json';

export default {
    name: "FooterSection",
    components: {
        PhoneChild: defineAsyncComponent(() => import('./PhoneChild.vue')),
        MailChild: defineAsyncComponent(() => import('./MailChild.vue')),
        AppointmentChild: defineAsyncComponent(() => import('./AppointmentChild.vue')),
        ContactChild: defineAsyncComponent(() => import('./ContactChild.vue')),
    },
    data() {
        return {
            activeComponent: "",
            lastLockedComponent: "",
            hoverTimer: null,
            githubUrl: home.github.url,
            githubCreatedAt: null,
        }
    },
    computed: {
        profileAgeLabel() {
            const currentYear = new Date().getFullYear();
            const createdYear = this.githubCreatedAt ? new Date(this.githubCreatedAt).getFullYear() : currentYear;

            if (createdYear === currentYear) {
                return `0xeightnine · ${createdYear}`;
            }

            return `0xeightnine · ${createdYear}–${currentYear}`;
        },
        computedDataDictionary() {
            let data = {};
            // Logic to return dynamic data based on activeComponent
            switch (this.activeComponent) {
                case 'PhoneChild':
                    data = {
                        "title": home.mobile.title.charAt(0).toUpperCase() + home.mobile.title.slice(1),
                        "number": home.mobile.number
                    }
                    break;
                case 'MailChild':
                    data = {
                        "title": home.mail.title.charAt(0).toUpperCase() + home.mail.title.slice(1),
                    }
                    break;
                case 'AppointmentChild':
                    data = {
                        "title": home.appointment.title.charAt(0).toUpperCase() + home.appointment.title.slice(1),
                        "meetingTypes": home.appointment.meeting_types
                    }
                    break;
                case 'ContactChild':
                    data = {
                        "title": home.contact.title.charAt(0).toUpperCase() + home.contact.title.slice(1),
                        "socials": home.contact.socials
                    }
                    break;
                default:
                    data = {
                        title: "Pi Teams is working!"
                    }
                    break;
            }
            return data;
        }
    },
    mounted() {
        this.fetchGitHubProfile();
    },
    methods: {
        async fetchGitHubProfile() {
            try {
                const response = await fetch('https://api.github.com/users/0xeightnine', {
                    headers: {
                        Accept: 'application/vnd.github+json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`GitHub API request failed with status ${response.status}`);
                }

                const user = await response.json();
                this.githubCreatedAt = user.created_at || null;
            } catch (error) {
                console.warn('Unable to load GitHub creation year for footer.', error);
            }
        },
        setActive(name) {
            this.clearTimer();
            this.activeComponent = name;
            this.lastLockedComponent = name;
            this.removeClickOutsideListener(); // Remove existing listener before adding a new one
            this.addClickOutsideListener();
        },
        setActivePreview(name) {
            this.clearTimer();
            this.activeComponent = name;
        },
        setActivePreviewDestroy() {
            this.hoverTimer = setTimeout(() => {
                this.activeComponent = this.lastLockedComponent;
            }, 3000);
        },
        clearTimer() {
            if (this.hoverTimer) clearTimeout(this.hoverTimer);
        },
        dismissModal() {
            this.activeComponent = '';
            this.removeClickOutsideListener();
        },
        handleClickOutside(event) {
            const modalContainer = this.$refs.modalContainer;
            if (modalContainer && !modalContainer.contains(event.target)) {
                this.dismissModal();
            }
        },
        addClickOutsideListener() {
            this.$nextTick(() => {
                setTimeout(() => {
                    document.addEventListener('click', this.handleClickOutside);
                }, 0);
            });
        },
        removeClickOutsideListener() {
            document.removeEventListener('click', this.handleClickOutside);
        }
    },
    beforeUnmount() {
        this.removeClickOutsideListener();
    }
}
</script>
